import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pagination, Popconfirm, message } from "antd";

import { FaBell, FaRegCircleCheck } from "react-icons/fa6";
import { BsXCircle } from "react-icons/bs";

import { StudentAction, NotificationsAction } from "../../store/actions";

import {
  boxContainer,
  Spacer,
  inputFields,
  Buttons,
  popover,
  tables,
  Modal,
} from "../../components";

import { UtilityArrays, Utils } from "../../config";

import StudentsTopPart from "./students-top-part";

const Students = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { students, count, isLoading } = useSelector((state) => state.students);
  const { onlineUsers } = useSelector((state) => state.others);
  const profileData = useSelector((state) => state.profile?.user);
  const schoolId =
    profileData?.role === "school"
      ? profileData?.schoolId
      : profileData?.staff?.schoolId;

  const [notificationModal, setNotificationModal] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [notification, setNotification] = useState({
    text: "",
    title: "",
  });
  const [filters, setFilters] = useState({
    search: "",
    limit: 10,
    offset: 0,
    status: "",
    grade: "",
  });

  useEffect(() => {
    if (schoolId) {
      handleSearch();
    }
  }, [filters, schoolId]);

  const onShowSizeChange = (current, pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      limit: pageSize,
      offset: (current - 1) * pageSize,
    }));
    setPageNo(current);
  };

  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    setFilters((prevFilters) => ({
      ...prevFilters,
      offset: (pageNumber - 1) * prevFilters.limit,
    }));
  };

  const handleFilterChange = (updatedFilter) => {
    setPageNo(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilter,
      offset: 0,
    }));
  };

  const handleSearch = async () => {
    const payload = { ...filters, schoolId };
    dispatch(StudentAction.getAllStudents(payload));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleReset = async () => {
    setFilters({
      ...filters,
      search: "",
      limit: 10,
      offset: 0,
      status: "",
      grade: "",
    });
    setPageNo(1);
  };

  const handleNotify = () => {
    if (!notification?.title || !notification?.text) {
      message.error("Please fill all the fields");
      return;
    } else {
      const payload = { ...notification, schoolId };
      dispatch(NotificationsAction.addNotificationsMultiple(payload));
      setNotificationModal(false);
      setNotification({ title: "", text: "" });
    }
  };

  const handleUpdate = (userId, data) => {
    const payload = { userId, data, schoolId };
    dispatch(StudentAction.updateStudent(payload, () => handleSearch()));
  };

  const statusArray = [
    { title: "All", value: "" },
    { title: "Active", value: "active" },
    { title: "Pending", value: "pending" },
  ];

  const filteredGradeArray = UtilityArrays?.gradeArray?.map((item) => ({
    title: item,
    value: item,
  }));

  const filterButtons = [
    {
      title: "Status",
      items: (
        <div className="filter-dropdown">
          {statusArray?.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                handleFilterChange({ status: item.value });
              }}
              className={`${
                filters.status === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Year",
      items: (
        <div className="filter-dropdown">
          {filteredGradeArray?.map((item, index) => (
            <div
              key={index}
              onClick={() => handleFilterChange({ grade: item.value })}
              className={`${
                filters.grade === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: " ",
      dataIndex: "index",
      key: "0",
      width: 5,
      render: (text, record, index) => (
        <div className="flex gap-2 items-center">{index + 1}</div>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "1",
      width: 250,
      render: (text, record) => (
        <div className="flex gap-2 items-center">
          <img
            src={record?.profileImage}
            className="h-[3.75em] w-[3.75em] border border-searchBorder shadow-boxContainer rounded-lg object-contain bg-white"
          />

          <div className="font-semibold">
            {record?.firstName} {record?.lastName}
          </div>
        </div>
      ),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "10",
      width: 180,
      align: "center",
      render: (text, record) => (
        <div className="w-[15.625em]">
          <inputFields.SelectDropdown
            items={Utils.filterArray(UtilityArrays?.gradeArray)}
            value={record?.student?.grade ?? null}
            onChange={(e) =>
              handleUpdate(record?.id, {
                student: { update: { grade: e } },
              })
            }
          />
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "2",
      width: 100,
      align: "center",
      render: (text, record) => <div>{record?.email ?? "NA"}</div>,
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "4",
      width: 120,
      align: "center",
      render: (text, record) => (
        <div>{record?.phoneNumber ? `+${record?.phoneNumber}` : "NA"}</div>
      ),
    },
    {
      title: "Preferred Country",
      dataIndex: "subject",
      key: "5",
      width: 120,
      align: "center",
      render: (text, record) => (
        <div>{record?.questionnaire?.countryPreference[0] ?? "NA"}</div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "6",
      width: 150,
      align: "center",
      render: (text, record) => {
        let status = record.status;
        if (onlineUsers?.includes(record?.id)) {
          status = "Online";
        }
        return (
          <div className="flex gap-4 items-center">
            <div
              className={`${
                status?.toLowerCase() === "active"
                  ? "text-activeText"
                  : status?.toLowerCase() === "pending"
                  ? "text-pendingText"
                  : status?.toLowerCase() === "deleted"
                  ? "text-red-600"
                  : "text-onlineText"
              } capitalize`}
            >
              {status ?? "NA"}
            </div>
            {status?.toLowerCase() === "pending" && (
              <div className="flex gap-2 text-[1.5em]">
                <Popconfirm
                  title="Activate student"
                  description="Are you sure you want to activate this student?"
                  onConfirm={() =>
                    handleUpdate(record?.id, {
                      status: "active",
                    })
                  }
                  okText="Yes"
                  cancelText="No"
                  placement="topLeft"
                >
                  <FaRegCircleCheck className="text-main" />
                </Popconfirm>
                <Popconfirm
                  title="Delete student"
                  description="Are you sure you want to delete this student?"
                  onConfirm={() =>
                    handleUpdate(record?.id, {
                      status: "deleted",
                    })
                  }
                  okText="Yes"
                  cancelText="No"
                  placement="topLeft"
                >
                  <BsXCircle className="text-red-600" />
                </Popconfirm>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="text-black">
      <StudentsTopPart />
      <Spacer height={20} />
      <div className="flex gap-3 justify-center">
        <inputFields.DefaultSearchBox
          onChange={(e) => {
            handleFilterChange({ search: e.target.value });
          }}
          value={filters.search}
          onKeyDown={handleKeyDown}
        />
        <div className="w-[9.375em]">
          <Buttons.DefaultButton
            width="full"
            style={{ height: "100%" }}
            size="medium"
            onClick={() => handleSearch()}
          >
            Search
          </Buttons.DefaultButton>
        </div>
      </div>
      <Spacer height={10} />
      <div className="flex justify-between">
        <div className="flex gap-2 items-center">
          <div className="text-[0.875em] opacity-80">Filter:</div>
          {filterButtons?.map((item, index) => (
            <popover.WhitePopover
              content={item.items}
              placement={item?.placement ?? "bottom"}
              key={index}
            >
              <Buttons.DefaultButton size="small">
                {item?.title}
              </Buttons.DefaultButton>
            </popover.WhitePopover>
          ))}
        </div>
        {Object.values(filters).some(
          (value) => value !== "" && value?.length > 0
        ) && (
          <div className="font-semibold cursor-pointer" onClick={handleReset}>
            Clear Filters
          </div>
        )}
      </div>
      <Spacer height={10} />
      <boxContainer.LiteBoxContainer addstyle={"!p-0"}>
        <div>
          <div className="py-3 px-4 flex justify-between items-center">
            <div className="font-bold pl-4">
              Students logged in using code provided to school by the UniVision
              admin team
            </div>
            <div
              className="bg-main rounded-lg text-white font-semibold flex gap-2 py-2 px-3 text-[0.875em] items-center leading-3 cursor-pointer"
              onClick={() => setNotificationModal(true)}
            >
              <FaBell className="text-pendingText text-[1em]" /> Notify To All
            </div>
          </div>
          <tables.DefaultTable
            columns={columns}
            data={students}
            borderradius={0}
            rowKey={() => Math.random()}
            loading={isLoading}
            onRow={(record, index) => ({
              onClick: (event) => {
                const clickedElement = event.target.closest("td");
                const columnIndex = Array.from(
                  clickedElement?.parentNode?.children || []
                ).indexOf(clickedElement);

                if (![2, 6].includes(columnIndex)) {
                  if (event.ctrlKey) {
                    window.open(`/students/general/${record.id}`, "_blank");
                  } else {
                    navigate(`/students/general/${record.id}`);
                  }
                }
              },
            })}
          />
        </div>
        <div className="flex justify-center items-center p-3">
          <Pagination
            responsive
            hideOnSinglePage={false}
            current={pageNo}
            pageSize={filters.limit}
            total={count}
            showSizeChanger
            pageSizeOptions={UtilityArrays?.pageSizes}
            onShowSizeChange={onShowSizeChange}
            onChange={handlePageChange}
          />
        </div>
      </boxContainer.LiteBoxContainer>
      <Modal.DefaultModal
        open={notificationModal}
        onCancel={() => setNotificationModal(false)}
      >
        <div className="flex flex-col items-center justify-center gap-4 px-5 py-4 text-center text-[1.125em] w-[37.5em]">
          <div className="text-main text-[1.5em] font-semibold">
            Notification For Students
          </div>

          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col justify-left items-start gap-2">
              <div className="text-[1.125em] font-semibold pl-2">
                Notification Title
              </div>
              <inputFields.DefaultInput
                value={notification?.title}
                placeholder="Enter title"
                onChange={(e) =>
                  setNotification({ ...notification, title: e.target.value })
                }
              />
            </div>
            <div className="flex flex-col justify-left items-start gap-2">
              <div className="text-[1.125em] font-semibold pl-2">
                Notification Description
              </div>
              <inputFields.DefaultTextbox
                value={notification?.text}
                placeholder="Enter description"
                onChange={(e) =>
                  setNotification({ ...notification, text: e.target.value })
                }
              />
            </div>
          </div>
          <Spacer height={10} />

          <Buttons.DefaultButton onClick={handleNotify}>
            Send Notification
          </Buttons.DefaultButton>
        </div>
      </Modal.DefaultModal>
    </div>
  );
};

export default Students;
